import { Modal, ModalProps } from './Modal';
import { ReactElement, ReactNode } from 'react';
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Heading,
  Icon,
  Text,
  useDisclosure,
  useMultiStyleConfig,
  VStack,
} from '@chakra-ui/react';
import {
  AiOutlineCheckCircle,
  AiOutlineWarning,
  AiOutlineArrowRight,
  AiOutlineClose,
} from 'react-icons/ai/index.js';
import { IconType } from 'react-icons';

export interface ProgressStep {
  id: number;
  label: string;
  errorMessage?: string;
}

export interface StickyProgressProps {
  steps: ProgressStep[];
  mainCta: string;
  secondaryCta: string;
  activeStep: number | undefined;
  invalidStep?: boolean;
  submittingStep?: boolean;
  onMainAction?: () => void;
  onSecondaryAction?: () => void;
  isCancel?: boolean;
  cancel?: ReactNode;
  cancelModalProps?: Omit<ModalProps, 'isOpen' | 'onClose' | 'children'>;
  cancelTitle?: string;
  cancelText?: string | ReactElement;
  cancelConfirmText?: string;
  onCancelAction?: () => void;
  onStepSelection?: (stepId: number) => void;
  progress?: number;
  containerProps?: BoxProps;
  stepProps?: BoxProps;
  mainButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  secondaryButtonProps?: ButtonProps;
  cancelIcon?: IconType;
  submitIcon?: IconType;
}

export const StickyProgress = (props: StickyProgressProps) => {
  const {
    steps,
    mainCta,
    secondaryCta,
    activeStep,
    submittingStep,
    onMainAction,
    onSecondaryAction,
    isCancel,
    cancel = 'Cancel',
    cancelModalProps,
    cancelTitle = 'Cancel',
    cancelText = '',
    cancelConfirmText = 'Ok',
    onStepSelection,
    onCancelAction = () => null,
    progress = 0,
    containerProps,
    stepProps,
    mainButtonProps,
    cancelButtonProps,
    secondaryButtonProps,
    cancelIcon = AiOutlineClose,
    submitIcon = AiOutlineArrowRight,
  } = props;
  const styles = useMultiStyleConfig('StickyProgress', {});
  const { isOpen, onOpen, onClose } = useDisclosure();
  const onStepClick = (stepId: number) => {
    onStepSelection && onStepSelection(stepId);
  };

  return (
    <Box sx={styles.block} {...containerProps}>
      {steps.map((step, i) => {
        const isStepActive = step.id === activeStep;
        const isInvalid = step && (step?.errorMessage as string)?.length > 0;
        const stepCompleted = !isInvalid && activeStep && activeStep > i + 1;
        return (
          <Box
            key={`step-${i}`}
            sx={{ ...styles.stepRow, ...stepProps }}
            onClick={() => onStepClick(i + 1)}
            cursor={onStepSelection && !isStepActive ? 'pointer' : 'unset'}
          >
            {isStepActive && (
              <Box
                sx={styles.activeIndicator}
                borderTopLeftRadius={i ? '0' : '6px'}
              ></Box>
            )}
            <Heading as='p' size='h6'>
              {step.label}
            </Heading>
            {isInvalid && (
              <>
                <Text textStyle='small' textColor={'red.500'}>
                  {step.errorMessage}
                </Text>
                <Box sx={styles.iconIndicator}>
                  <Icon
                    as={AiOutlineWarning}
                    color='#E4002B'
                    sx={styles.icon}
                  />
                </Box>
              </>
            )}
            {stepCompleted && (
              <Box sx={styles.iconIndicator}>
                <Icon
                  as={AiOutlineCheckCircle}
                  color='#2FBA78'
                  sx={styles.icon}
                />
              </Box>
            )}
          </Box>
        );
      })}
      <VStack spacing='4' sx={styles.blockFooter}>
        <Button
          isLoading={submittingStep}
          type='submit'
          onClick={onMainAction}
          loadingText={progress > 0 ? `Loaded ${progress.toFixed(2)}%` : ''}
          {...mainButtonProps}
          variant='solid'
          leftIcon={<Icon as={submitIcon} />}
        >
          {mainCta}
        </Button>
        {isCancel ? (
          <Button
            onClick={onOpen}
            variant='outline'
            {...cancelButtonProps}
            leftIcon={<Icon as={cancelIcon} />}
          >
            {cancel}
          </Button>
        ) : (
          <>
            {secondaryCta && onSecondaryAction && (
              <Button
                onClick={onSecondaryAction}
                variant='outline'
                {...secondaryButtonProps}
              >
                {secondaryCta}
              </Button>
            )}
          </>
        )}
      </VStack>
      <Modal
        header={cancelTitle}
        showCancel={true}
        okText={cancelConfirmText}
        onOk={() => {
          onCancelAction();
          onClose();
        }}
        cancelText={cancel}
        isCentered
        size='xl'
        headerProps={{ as: 'h1', pr: 4 }}
        bodyProps={{ p: 6 }}
        cancelButtonProps={{ variant: 'secondary' }}
        {...cancelModalProps}
        isOpen={isOpen}
        onClose={() => {
          onClose();
        }}
      >
        {cancelText}
      </Modal>
    </Box>
  );
};
